import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaBell, FaBriefcase, FaMapMarkerAlt, FaSearch, FaUsers } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import api from '../services/api';
import { getJobLocationLabel } from '../utils/jobLocationPresentation';
import { formatCompactJobPay } from '../utils/jobPayPresentation';
import {
  HIRE_ROLES,
  MARKET_DATA,
  getGigRole,
  getJobQuery,
  getMarket,
} from '../data/seoLandingData';

interface Job {
  id: number;
  title: string;
  company: string;
  location: string | null;
  source: string;
  rate?: string | null;
  rate_min?: number | null;
  rate_max?: number | null;
  rate_type?: string | null;
  scraped_at?: string;
}

function formatRate(job: Job) {
  return formatCompactJobPay(job);
}

const RoleCityGigsPage: React.FC = () => {
  const { city, role: roleSlug } = useParams<{ city: string; role: string }>();
  const market = getMarket(city);
  const role = getGigRole(roleSlug);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!market || !role) return;
    setLoading(true);
    api.get(`/scraped-jobs?${getJobQuery(market, role, 12)}`)
      .then((res: any) => {
        const data = res.data?.jobs ?? res.data ?? [];
        setJobs(Array.isArray(data) ? data : []);
      })
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [market, role]);

  if (!market || !role) {
    return <Navigate to="/jobs" replace />;
  }

  const pageUrl = `https://stagecrew.app/gigs/${market.slug}/${role.gigSlug}`;
  const title = `${role.label} Jobs in ${market.name}, ${market.stateAbbr} | StageCrew`;
  const description = `Find ${role.label.toLowerCase()} jobs and live event gigs in ${market.name}, ${market.stateAbbr}. ${role.description} Browse local crew calls and get matched on StageCrew.`;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title={title}
        description={description}
        url={pageUrl}
        keywords={`${role.label} jobs ${market.name}, ${role.label} gigs ${market.name}, ${market.name} ${role.label}, live event jobs ${market.name}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${role.label} jobs in ${market.name}`,
          description,
          url: pageUrl,
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: jobs.length,
            itemListElement: jobs.slice(0, 20).map((job, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              url: `https://stagecrew.app/jobs/${job.id}`,
              name: job.title,
            })),
          },
        }}
      />

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/jobs" className="hover:text-blue-700">Jobs</Link>
            <span>/</span>
            <Link to={`/gigs/${market.slug}`} className="hover:text-blue-700">{market.name}</Link>
            <span>/</span>
            <span>{role.label}</span>
          </div>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 mb-4">
              <FaMapMarkerAlt /> {market.name}, {market.stateAbbr}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-950 tracking-normal mb-5">
              {role.label} jobs in {market.name}
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl">
              {role.description} StageCrew groups {market.name} role pages around sourced job inventory, nearby vendors, and local market context.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/register?role=worker" className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800">
                <FaBell /> Get matched
              </Link>
              <Link to={`/hire/${role.slug}/${market.slug}`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-700">
                <FaUsers /> Hiring {role.pluralLabel}?
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaBriefcase className="text-blue-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Role scope</h2>
          <p className="text-sm text-slate-600 mt-2">{role.buyerNeed}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaSearch className="text-emerald-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Rate guidance</h2>
          <p className="text-sm text-slate-600 mt-2">{role.dayRate}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaMapMarkerAlt className="text-amber-600 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Local context</h2>
          <p className="text-sm text-slate-600 mt-2">{market.localInsight}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-12">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-2xl font-bold text-slate-950">Recent {role.label.toLowerCase()} gigs</h2>
          <Link to={`/gigs/${market.slug}`} className="text-sm font-semibold text-blue-700 hover:underline">All {market.name} gigs</Link>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map(item => (
              <div key={item} className="rounded-lg border border-slate-200 bg-white p-5 animate-pulse">
                <div className="h-5 w-2/3 bg-slate-200 rounded mb-3" />
                <div className="h-4 w-1/2 bg-slate-100 rounded" />
              </div>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
            <h2 className="text-xl font-bold text-slate-950">No sourced {role.label.toLowerCase()} listings found right now</h2>
            <p className="text-slate-600 mt-2">Browse all {market.name} gigs or create a worker profile so new calls can reach you.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              <Link to={`/gigs/${market.slug}`} className="rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800">
                Browse {market.name} gigs
              </Link>
              <Link to="/register?role=worker" className="rounded-lg border border-slate-300 px-4 py-2 font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-700">
                Join StageCrew
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {jobs.map(job => (
              <Link key={job.id} to={`/jobs/${job.id}`} className="rounded-lg border border-slate-200 bg-white p-5 hover:border-blue-400 hover:shadow-sm transition">
                <div className="font-bold text-slate-950">{job.title}</div>
                <div className="text-sm text-slate-500 mt-1">
                  {job.company} - {getJobLocationLabel(job)}
                </div>
                <div className="text-sm font-semibold text-emerald-700 mt-3">{formatRate(job)}</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-slate-950 mb-4">Other {market.name} role pages</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.values(HIRE_ROLES).filter(other => other.gigSlug !== role.gigSlug).slice(0, 8).map(other => (
                <Link key={other.gigSlug} to={`/gigs/${market.slug}/${other.gigSlug}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4 hover:border-blue-400 hover:bg-blue-50 transition">
                  <div className="font-semibold text-slate-950">{other.label} jobs</div>
                  <div className="text-sm text-slate-500 mt-1">{other.dayRate}</div>
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-950 mb-4">Nearby markets</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {market.nearbyMarkets.map(slug => {
                const nearby = MARKET_DATA[slug];
                if (!nearby) return null;
                return (
                  <Link key={slug} to={`/gigs/${nearby.slug}/${role.gigSlug}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4 hover:border-blue-400 hover:bg-blue-50 transition">
                    <div className="font-semibold text-slate-950">{nearby.name}</div>
                    <div className="text-sm text-slate-500 mt-1">{role.label} jobs</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RoleCityGigsPage;
